import { motion, AnimatePresence } from "framer-motion";
import { useMatch } from "@/match/MatchContext";
import { PENALTIES, SHOOTOUT_INTRO } from "@/data/content";

function PenaltyDots({ results, total, currentIndex }) {
  return (
    <div className="flex items-center justify-center gap-2.5" data-testid="penalty-dots">
      {Array.from({ length: total }).map((_, i) => {
        const r = results[i];
        const isCurrent = i === currentIndex && r === undefined;
        let cls = "bg-white border-black";
        if (r === "advaith") cls = "bg-[#84E116] border-black";
        if (r === "life") cls = "bg-[#FF3B30] border-black";
        if (isCurrent) cls = "bg-white border-black";
        return (
          <span
            key={i}
            className={`w-4 h-4 sm:w-5 sm:h-5 border-[3px] ${cls} ${isCurrent ? "animate-pulse" : ""}`}
            title={r ? (r === "advaith" ? "Scored" : "Saved") : "Pending"}
          />
        );
      })}
    </div>
  );
}

export default function Shootout() {
  const { shootout, startShootout, selectOption, takeShot, nextPenalty, scrollTo } = useMatch();
  const { status, qIndex, selected, revealed, results } = shootout;
  const penalty = PENALTIES[Math.min(qIndex, PENALTIES.length - 1)];

  return (
    <div className="pitch-stripes relative border-b-4 border-black py-20 sm:py-28 px-4 sm:px-8 lg:px-16 overflow-hidden" data-testid="shootout-section">
      {/* goal frame hint */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <rect x="330" y="90" width="540" height="330" fill="none" stroke="#FFFFFF" strokeWidth="5" opacity="0.5" />
        <line x1="330" y1="420" x2="330" y2="800" stroke="#FFFFFF" strokeWidth="5" opacity="0.3" />
        <line x1="870" y1="420" x2="870" y2="800" stroke="#FFFFFF" strokeWidth="5" opacity="0.3" />
        <circle cx="600" cy="620" r="6" fill="#FFFFFF" opacity="0.6" />
      </svg>

      <div className="relative max-w-4xl mx-auto">
        {status !== "playing" && status !== "finished" && (
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            data-testid="shootout-intro"
          >
            <span className="inline-block bg-[#0A0A0A] text-[#84E116] font-broadcast font-bold text-[10px] sm:text-xs tracking-[0.3em] uppercase px-4 py-2 mb-8">
              The Penalty Shootout
            </span>
            <div className="bg-white border-[3px] border-black hard-shadow-lg p-7 sm:p-10 mb-8">
              {SHOOTOUT_INTRO.lines.map((line, i) => (
                <p
                  key={i}
                  className={`font-display uppercase leading-tight tracking-tight text-black ${
                    i === 0
                      ? "text-2xl sm:text-4xl mb-4"
                      : i === 1
                        ? "text-xl sm:text-2xl mb-4"
                        : "text-lg sm:text-xl text-black/70"
                  }`}
                >
                  {line}
                </p>
              ))}
            </div>
            <button
              type="button"
              onClick={startShootout}
              data-testid="start-shootout-btn"
              className="font-display uppercase text-lg sm:text-xl tracking-wide bg-[#0052FF] text-white border-[3px] border-black hard-shadow-lg px-10 py-4 transition-all duration-200 hover:bg-[#0041CC] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[8px_8px_0_0_#000] active:translate-x-1 active:translate-y-1 active:shadow-none"
            >
              Step Up To The Spot
            </button>
          </motion.div>
        )}

        {status === "playing" && (
          <div data-testid="shootout-arena">
            <AnimatePresence mode="wait">
              <motion.div
                key={penalty.id}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="text-center mb-8">
                  <span
                    className="inline-block bg-[#0A0A0A] text-[#84E116] font-broadcast font-bold text-[10px] sm:text-xs tracking-[0.3em] uppercase px-4 py-2"
                    data-testid="penalty-number"
                  >
                    Penalty {qIndex + 1} / {PENALTIES.length}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-8">
                  {/* Life asks */}
                  <div className="bg-[#FFC7DA] border-[3px] border-black hard-shadow-lg p-6 sm:p-8" data-testid="life-question-card">
                    <span className="font-broadcast font-extrabold text-[10px] sm:text-xs tracking-[0.25em] uppercase text-black/60 block mb-3">
                      Life asks
                    </span>
                    <p className="font-display uppercase text-xl sm:text-2xl leading-tight tracking-tight text-black">
                      {penalty.question}
                    </p>
                  </div>

                  {/* Advaith answers */}
                  <div className="bg-white border-[3px] border-black hard-shadow-lg p-6 sm:p-8" data-testid="answers-card">
                    <span className="font-broadcast font-extrabold text-[10px] sm:text-xs tracking-[0.25em] uppercase text-black/60 block mb-4">
                      Advaith answers
                    </span>
                    <div className="flex flex-col gap-3">
                      {penalty.options.map((opt, i) => {
                        const isChosen = selected === i;
                        const isDimmed = revealed && !isChosen;
                        return (
                          <button
                            key={i}
                            type="button"
                            disabled={revealed}
                            onClick={() => selectOption(i)}
                            data-testid={`answer-option-${i}`}
                            className={`text-left border-[3px] border-black px-4 py-3 font-broadcast font-bold text-xs sm:text-sm leading-snug transition-all duration-150 ${
                              isChosen
                                ? "bg-[#0052FF] text-white"
                                : "bg-white text-black hover:bg-[#F2F2F0]"
                            } ${isDimmed ? "opacity-40" : ""} ${
                              !revealed && !isChosen
                                ? "hover:-translate-y-0.5 hover:shadow-[4px_4px_0_0_#000]"
                                : ""
                            } disabled:cursor-default`}
                          >
                            {opt.text}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* feedback */}
                <AnimatePresence>
                  {revealed && (
                    <motion.div
                      className="mb-8"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35 }}
                      data-testid="penalty-feedback"
                    >
                      <div
                        className={`border-[3px] border-black hard-shadow px-5 py-4 text-center font-broadcast font-extrabold text-xs sm:text-sm tracking-[0.15em] uppercase ${
                          results[results.length - 1] === "advaith"
                            ? "bg-[#84E116] text-black"
                            : "bg-[#FF3B30] text-white"
                        }`}
                      >
                        {penalty.reaction}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex flex-col items-center gap-6">
                  <PenaltyDots results={results} total={PENALTIES.length} currentIndex={qIndex} />
                  {!revealed ? (
                    <button
                      type="button"
                      onClick={takeShot}
                      disabled={selected === null}
                      data-testid="take-shot-btn"
                      className="font-display uppercase text-lg sm:text-xl tracking-wide bg-[#0052FF] text-white border-[3px] border-black hard-shadow-lg px-10 py-4 transition-all duration-200 enabled:hover:bg-[#0041CC] enabled:hover:-translate-x-0.5 enabled:hover:-translate-y-0.5 enabled:hover:shadow-[8px_8px_0_0_#000] enabled:active:translate-x-1 enabled:active:translate-y-1 enabled:active:shadow-none disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Take The Shot
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        const isLast = qIndex + 1 >= PENALTIES.length;
                        nextPenalty();
                        if (isLast) setTimeout(() => scrollTo("#final"), 350);
                      }}
                      data-testid="next-penalty-btn"
                      className="font-display uppercase text-lg sm:text-xl tracking-wide bg-[#0A0A0A] text-[#84E116] border-[3px] border-black hard-shadow-lg px-10 py-4 transition-all duration-200 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[8px_8px_0_0_#000] active:translate-x-1 active:translate-y-1 active:shadow-none"
                    >
                      {qIndex + 1 >= PENALTIES.length ? "See The Final Result" : "Next Penalty"}
                    </button>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
